import { chainDynamicValue, useComponentState } from "@faedev/dynamic-values";
import { composeClassNames, ifDefined } from "@faedev/editor-utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import FormJoinUsBasic from "src/components/FormJoinUsBasic";
import FormJoinUsClasses from "src/components/FormJoinUsClasses";
import FormJoinUsCommunication from "src/components/FormJoinUsCommunication";
import FormJoinUsPhotos from "src/components/FormJoinUsPhotos";
import FormJoinUsPrices from "src/components/FormJoinUsPrices";
import FormJoinUsSites from "src/components/FormJoinUsSites";
import FormJoinUsYogaProfile from "src/components/FormJoinUsYogaProfile";
import Translate from "src/components/Translate";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import isFormValid from "src/functions/isFormValid";
import onChangeToValueCheckbox from "src/functions/onChangeToValueCheckbox";
import sendFormByEmail from "src/functions/sendFormByEmail";
import useEffect from "src/functions/useEffect";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormGroup } from "src/styles/sFormGroup.module.scss";
import { sFormJoinUs } from "src/styles/sFormJoinUs.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import { useLocalStorageState } from "use-local-storage-state";

type Props = { style?: CSSProperties | null | undefined };

function FormJoinUs({ style }: Props) {
  const { locale: lang } = useRouter();
  var [resultState, setResultState] = useState(null);
  var [validated, setValidated] = useState(false);
  var formData = useComponentState({
    "content-paid": false,
    "instagram-implement": false,
  });
  var [localStorage, setStorage] = useLocalStorageState(
    `join-us-form-data`,
    formData.value
  );
  useEffect(function () {
    return (function iife() {
      var joinUsDataFromStorage = localStorage;
      joinUsDataFromStorage ? formData.change(joinUsDataFromStorage) : null;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(
    function () {
      return (function iife() {
        setStorage(formData.value);
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.value]
  );
  return (
    <Form
      className={composeClassNames([sFormJoinUs, "form-joinus"])}
      id={`FormJoinUs`}
      validated={validated}
      noValidate
    >
      <FormJoinUsBasic formData={formData} />
      <br />
      <FormJoinUsYogaProfile formData={formData} />
      <FormJoinUsSites formData={formData} />
      <FormJoinUsCommunication formData={formData} />
      <FormJoinUsPrices formData={formData} />
      <FormJoinUsPhotos />
      <FormJoinUsClasses formData={formData} />
      <br />
      <Form.Group
        className={composeClassNames([sFormGroup, `form-check-group`])}
        style={gutter(`margin`, `vertical`, `md`)}
      >
        <Form.Check.Input
          className={sFormControl}
          type={`checkbox`}
          name={`conditions`}
          id={`conditions`}
          onChange={function (param2) {
            return chainDynamicValue(formData, "contract-agreement").change(
              onChangeToValueCheckbox(param2)
            );
          }}
          required={true}
          checked={chainDynamicValue(formData, "contract-agreement").value}
        />
        <Form.Check.Label className={sFormLabel} htmlFor={`conditions`}>
          <Translate tKey={`I agree with`} />
          {` `}
          <Link
            href={getLink(lang, `terms-and-privacy`, null)}
            passHref
            locale={false}
          >
            <Button
              variant={`link`}
              type={`button`}
              as={`a`}
              className={composeClassNames([sButton, "btn-inline"])}
            >
              <Translate
                tKey={`business conditions and rules of personal data protection`}
              />
            </Button>
          </Link>
          {`.`}
        </Form.Check.Label>
        <Form.Control.Feedback type={`invalid`}>
          <Translate tKey={`Fill in this field, it is mandatory.`} />
        </Form.Control.Feedback>
      </Form.Group>
      <Button
        className={composeClassNames([sButton, "btn-cta"])}
        variant={`primary`}
        size={`lg`}
        onClick={function () {
          setValidated(true);
          if (isFormValid(`FormJoinUs`)) {
            setResultState(true);
            return sendFormByEmail(
              ``,
              `#FormJoinUs`,
              function () {
                return setResultState(false);
              },
              function () {
                setStorage(null);
              }
            );
          }
        }}
        style={gutter(`margin`, `vertical`, `md`)}
      >
        <Translate tKey={`Submit for processing`} />
      </Button>
      {validated && !isFormValid(`FormJoinUs`) ? (
        <Alert
          variant={`danger`}
          className={sAlert}
          style={gutter(`margin`, `vertical`, `md`)}
        >
          <Translate
            tKey={`Uh-oh. Not all required informations are filled in!`}
          />
        </Alert>
      ) : null}
      {ifDefined(
        resultState,
        (value) =>
          value ? (
            <Alert
              variant={`success`}
              className={sAlert}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <Translate tKey={`Great! The form was sent successfully.`} />
            </Alert>
          ) : (
            <Alert
              variant={`danger`}
              className={sAlert}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <Translate tKey={`Oops. The form could not be sent.`} />
            </Alert>
          ),
        () => null
      )}
    </Form>
  );
}

export default FormJoinUs;
